import {
	Fragment,
	useEffect,
	useCallback,
	useRef,
	RefObject
} from 'react';
import {
	H3,
	Spacer,
	Text,
	Tile,
	Caption,
	InfoChip,
	SuccessChip,
	ErrorChip,
	WarnChip,
	H4,
	Grid
} from '@components';
import { EditShipment } from '@state';
import { useRecoilState } from 'recoil';
import {
	TCargoEOS,
	CHIP_SIZE,
	TShiperETAReason,
	TPagedDialogContext,
	TShipperETAResponse,
	TShipmentOrder
} from '@types';
import styles from '../EditShipment.module.scss';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import ClockIcon from '@mui/icons-material/AccessTimeOutlined';
import { Request } from './UpdateETA.request';
import { formatDate as dateFormat, groupCargosByStop } from '@utils';

type TProps = {
	context?: TPagedDialogContext;
	pagesRef?: RefObject<HTMLElement>;
	reasons: TShiperETAReason[];
}

export const UpdateETA = (props: TProps) => {
	const { context, pagesRef, reasons } = props;
	const isScrollRef = useRef(-1);
	const subpages = useRef<(HTMLDivElement | null)[]>([]);
	const [shipment, setShipment] = useRecoilState(EditShipment);
	const { pickups: pickupGroups, dropoffs: dropoffGroups } = groupCargosByStop(shipment?.cargos ?? []);

	const formatDate = (date: string) => {
		return dateFormat(new Date(date), 'DATE_WITH_SHORT_MONTH_NAME');
	}

	const formatTime = (startTime: string, endTime: string) => {
		return `${startTime} - ${endTime}`;
	}

	const updateCargoETA = (eta: TShipperETAResponse, cargo: TCargoEOS, dest: 'pickup' | 'dropoff', index: number) => {
		const newCargo = { ...cargo }
		if (dest === 'pickup') {
			newCargo.pick_up_estimation_time_of_arrivals = [
				...(newCargo.pick_up_estimation_time_of_arrivals ?? []),
				eta
			];
		} else if (dest === 'dropoff') {
			newCargo.drop_off_estimation_time_of_arrivals = [
				...(newCargo.drop_off_estimation_time_of_arrivals ?? []),
				eta
			];
		}
		const updateShipment = {
			...shipment,
			cargos: shipment?.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return newCargo
			})
		} as TShipmentOrder;
		setShipment(updateShipment);
	}

	const generateStopTile = useCallback((cargo: TCargoEOS, dest: 'pickup' | 'dropoff', stopIndex: number, cargoIndex: number) => {
		const eta = dest === 'pickup' ? cargo.pick_up_estimation_time_of_arrivals : cargo.drop_off_estimation_time_of_arrivals;
		// The stop's own ordinal, falling back to render order for shipments booked before it was stored.
		const stopNumber = (dest === 'pickup' ? cargo.pick_position : cargo.drop_position) ?? stopIndex;

		return (
			<div ref={(el) => (subpages.current[stopIndex] = el)}>
				<Tile stretch>
					<div className={styles.row} style={{ justifyContent: 'space-between' }}>
						<Caption>Stop {stopNumber + 1}</Caption>
						<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} size={CHIP_SIZE.SMALL} />
					</div>
					<H4>{cargo[dest === 'pickup' ? 'pickup' : 'dropoff'].business_name}</H4>
					<Text>{cargo[dest === 'pickup' ? 'pickup' : 'dropoff'].address}</Text>
					<Spacer size={16} />
					{eta && eta.map((oneEta, etaIndex) => <Fragment key={etaIndex}>
						<Tile stretch hasBorder>
							<H4>Your requested booking time</H4>
							<Spacer size={12} />
							<Grid columns={3} minWidth={'100px'}>
								<Text>{formatDate(oneEta.request_arrival_date)}</Text>
								<div className={styles.centeredRow}>
									<Text>{formatTime(oneEta.request_arrival_start_time, oneEta.request_arrival_end_time)}</Text>
									<ClockIcon style={{ fontSize: '16px' }} />
								</div>
								<Grid.Item justifySelf={'end'}>
									{oneEta.is_approved && <SuccessChip size={CHIP_SIZE.SMALL} showIcon text={'Approved'} />}
									{oneEta.is_rejected && <ErrorChip size={CHIP_SIZE.SMALL} showIcon text={'Rejected'} />}
									{!oneEta.is_approved && !oneEta.is_rejected && <WarnChip size={CHIP_SIZE.SMALL} showIcon text={'Pending'} />}
								</Grid.Item>
							</Grid>
						</Tile>
						<Spacer size={16} />
					</Fragment>)}
					<Request
						destination={dest}
						reasons={reasons}
						statusCode={shipment?.status.code}
						cargo={cargo}
						updateETA={(eta) => updateCargoETA(eta, cargo, dest, cargoIndex)}
					/>
				</Tile>
			</div>
		)
	}, [shipment, reasons]);

	const onPagesScroll = () => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const curPos = pagesRef.current.scrollTop;
			isScrollRef.current = curPos;
			setTimeout(() => {
				if (curPos !== isScrollRef.current) {
					isScrollRef.current = -1;
				}
			}, 100);
			subpages.current.forEach((sp, i) => {
				if (sp) {
					const threshold = 200;
					if (curPos < sp.offsetTop && curPos > sp.offsetTop - threshold) {
						context.setActiveSubMenu(i);
					}
				}
			});
		}
	}

	useEffect(() => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const spTops = subpages.current.map((sp, i) => {
				if (i === 0) return 0;
				if (sp) return sp.offsetTop ?? 0;
				return 0;
			});
			if (isScrollRef.current < 0) {
				pagesRef.current?.scrollTo({
					top: context.activeSubMenu === 0 ? spTops[0] : spTops[context.activeSubMenu],
					behavior: 'smooth'
				});
			}
		}
		if (pagesRef && pagesRef.current) {
			pagesRef.current.addEventListener('scroll', onPagesScroll);
		}
		return () => {
			pagesRef && pagesRef.current && pagesRef.current.removeEventListener('scroll', onPagesScroll);
		}
	}, [context, pagesRef]);

	return (
		<>
			<H3>Booking Times</H3>
			<Spacer size={16} />
			<Text>Update arrival times and access details for both pick-up and drop-off locations to ensure smooth scheduling.</Text>
			<Spacer size={16} />
			{pickupGroups.map((group, groupIndex) => {
				return <Fragment key={groupIndex}>
					{generateStopTile((shipment as TShipmentOrder).cargos[group[0]], 'pickup', groupIndex, group[0])}
					<Spacer size={16} />
				</Fragment>
			})}
			{/* The drop-off panels take the slots after the pick-up panels, matching the menu order. */}
			{dropoffGroups.map((group, i) => (
				<Fragment key={`dropoff-${i}`}>
					{generateStopTile((shipment as TShipmentOrder).cargos[group[0]], 'dropoff', pickupGroups.length + i, group[0])}
				</Fragment>
			))}
		</>
	);
}
